"use client"

import { Stack, VStack } from "@chakra-ui/react"
import { type FC } from "react"
import { useId } from "react"

import BlurFade from "@/components/Animations/BlurFade"
import Carousel from "@/components/Carousel"
import MarkdownParser from "@/components/MarkdownParser"
import { type CarouselBlock as CarouselBlockType } from "@/types/database"

import ContentBlockTitle, {
  type ContentBlockTitleNumberProps,
} from "./ContentBlockTitle"

interface CarouselBlockProps extends ContentBlockTitleNumberProps {
  block: CarouselBlockType
}

const CarouselBlock: FC<CarouselBlockProps> = ({ block, titleNumber }) => {
  const { darker_bg, description, padding, show_legend, title } = block
  const slides = block.images.map((image) => ({
    slideDescription: image.directus_files_id.description ?? "",
    slideTitle: image.directus_files_id.title ?? "Slide",
    src: `${process.env.DIRECTUS_ASSETS_URL}${image.directus_files_id.id}?quality=100&width=1438`,
  }))
  const trimmedTitle = title?.trim()
  const hasTitle = Boolean(trimmedTitle)
  const hasDescription =
    description && description !== "" && description !== null
  // console.log('padding carousel', padding);
  const id = useId()
  return (
    <Stack
      alignItems="flex-start"
      aria-labelledby={`carousel-heading-${id}`}
      bgColor={darker_bg ? "bg.subtle" : "bg.paper"}
      className="carousel-block edge-to-edge"
      direction={{ base: "column" }}
      gap={8}
      overflow="hidden"
      position="relative"
      px={{
        base: padding ? 8 : 0,
        md: padding ? 10 : 0,
        lg: padding ? 24 : 0,
        xl: padding ? 40 : 0,
      }}
    >
      {hasTitle && (
        <VStack
          alignItems="flex-start"
          bgColor={darker_bg ? "bg.subtle" : "bg.paper"}
          className="carousel-title"
          flexGrow={1}
          pt={padding ? 0 : { base: 12, lg: 24 }}
          px={{
            base: padding ? 0 : 8,
            md: padding ? 0 : 10,
            lg: padding ? 0 : 24,
            xl: padding ? 0 : 40,
          }}
        >
          <BlurFade flexDirection="column" gap={0} inView>
            <ContentBlockTitle title={trimmedTitle} titleNumber={titleNumber} />
            {hasDescription && (
              <MarkdownParser
                color="fg.muted"
                fontSize="sm"
                markdown={description}
                pProps={{ fontFamily: "mono" }}
              />
            )}
          </BlurFade>
        </VStack>
      )}
      <VStack alignItems="center" position="relative" width="full">
        <BlurFade inView>
          <Carousel
            bgColor={darker_bg ? "bg.subtle" : "bg.paper"}
            padding={padding}
            showLegend={show_legend}
            slides={slides}
          />
        </BlurFade>
      </VStack>
    </Stack>
  )
}

export default CarouselBlock
